(() => {
  if (window.QuadroEUNavigation) return;
  const headers = new Map();
  const focusable = root => Array.from(root.querySelectorAll('a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),[tabindex="0"]')).filter(element => element.getClientRects().length && !element.closest('[hidden]'));
  const submit = form => { if (form?.requestSubmit) form.requestSubmit(); else form?.submit(); };
  const safeURL = value => {
    try { const url = new URL(value, location.origin); return ['https:', 'http:'].includes(url.protocol) ? url : null; } catch (error) { return null; }
  };
  function bindHeader(header) {
    if (headers.has(header)) return;
    const root = document.documentElement;
    const shell = header.closest('.shopify-section') || header.parentElement;
    const bar = header.querySelector('.site-header__mobile-nav');
    const menu = header.querySelector('#MobileNavWrapper');
    const menuRoot = header.querySelector('#MobileNav');
    const toggle = header.querySelector('#SiteNavButton');
    const desktop = header.querySelector('#AccessibleNav');
    const links = desktop?.querySelector('#SiteNav');
    const logo = bar?.querySelector('.site-header__logo');
    const icons = bar?.querySelector('.site-header__icons');
    const compactQuery = matchMedia('(max-width: 1024px)');
    const search = shell.querySelector('#SearchDrawer');
    const abort = new AbortController();
    const on = (element, event, callback, options = {}) => element?.addEventListener(event, callback, { ...options, signal: abort.signal });
    const mediaCleanups = [];
    const onMedia = (query, callback) => {
      if (typeof query.addEventListener === 'function') {
        query.addEventListener('change', callback);
        mediaCleanups.push(() => query.removeEventListener('change', callback));
      } else if (typeof query.addListener === 'function') {
        query.addListener(callback);
        mediaCleanups.push(() => query.removeListener(callback));
      }
    };
    let openMenu = false;
    let stack = [];
    let inertNodes = [];
    let resizeFrame = 0;
    let restoreSearchFocus = null;
    let stickyShell = false;
    let navReady = false;
    let banners = [];
    let disposed = false;
    let desktopFit = null;
    let desktopFitDirty = true;
    // R6: compare inline values, not computed styles. No layout read is needed
    // to avoid re-writing viewport hints on every resize/scroll notification.
    const setStyle = (element, name, value) => {
      if (element.style.getPropertyValue(name) !== value) element.style.setProperty(name, value);
    };
    const setAttribute = (element, name, value) => {
      if (element.getAttribute(name) !== value) element.setAttribute(name, value);
    };
    menu?.classList.add('eu-nav-ready');
    const renderMobile = () => {
      if (!menuRoot) return;
      menuRoot.querySelectorAll('li, .js-toggle-submenu').forEach(element => { element.hidden = false; });
      menuRoot.querySelectorAll('.mobile-nav__dropdown').forEach(panel => { panel.hidden = !stack.includes(panel); });
      menuRoot.querySelectorAll('[data-target]').forEach(button => button.setAttribute('aria-expanded', String(stack.some(panel => panel.dataset.parent === button.dataset.target))));
      [menuRoot, ...stack].forEach((list, index) => {
        const next = stack[index];
        if (!next) return;
        const branch = next.parentElement;
        Array.from(list.children).forEach(item => { item.hidden = item !== branch; });
        branch.querySelector(':scope > .js-toggle-submenu')?.setAttribute('hidden', '');
      });
    };
    const closeDesktop = except => desktop?.querySelectorAll('[data-has-dropdowns]').forEach(item => {
      if (item === except) return;
      item.classList.remove('site-nav--active-dropdown');
      item.querySelector(':scope > button')?.setAttribute('aria-expanded', 'false');
    });
    const closeMobile = (focus = true) => {
      if (!openMenu) return;
      openMenu = false;
      menu.hidden = true;
      menu.classList.remove('js-menu--is-open');
      toggle.classList.add('mobile-nav--open');
      toggle.classList.remove('mobile-nav--close');
      toggle.setAttribute('aria-expanded', 'false');
      document.documentElement.classList.remove('eu-menu-open');
      inertNodes.forEach(([element, inert]) => { element.inert = inert; });
      inertNodes = [];
      stack = [];
      renderMobile();
      if (focus) toggle.focus();
    };
    const openMobile = () => {
      if (!menu || !toggle) return;
      closeDesktop();
      openMenu = true;
      menu.hidden = false;
      menu.classList.add('js-menu--is-open');
      toggle.classList.remove('mobile-nav--open');
      toggle.classList.add('mobile-nav--close');
      toggle.setAttribute('aria-expanded', 'true');
      document.documentElement.classList.add('eu-menu-open');
      const inertTargets = Array.from(document.querySelectorAll('main'));
      const footerSection = document.querySelector('footer.site-footer')?.closest('.shopify-section');
      if (footerSection && !inertTargets.includes(footerSection)) inertTargets.push(footerSection);
      inertNodes = inertTargets.map(element => [element, element.inert]);
      inertNodes.forEach(([element]) => { element.inert = true; });
      renderMobile();
      focusable(menu)[0]?.focus();
    };
    const outerWidth = (element, includeOverflow = true) => {
      if (!element) return 0;
      const style = getComputedStyle(element);
      return Math.ceil(Math.max(element.getBoundingClientRect().width, includeOverflow ? element.scrollWidth : 0) + (parseFloat(style.marginLeft) || 0) + (parseFloat(style.marginRight) || 0));
    };
    // These helpers only read. Banner/viewport measurements are collected
    // before any of the CSS variables used by the header are changed.
    const viewportMetrics = () => {
      const viewport = window.visualViewport;
      const layoutWidth = Math.max(1, root.clientWidth || window.innerWidth || 1);
      const layoutHeight = Math.max(1, root.clientHeight || window.innerHeight || 1);
      const width = Math.max(1, viewport?.width || layoutWidth);
      const height = Math.max(1, viewport?.height || layoutHeight);
      const left = Math.max(0, viewport?.offsetLeft || 0);
      const ratio = width / height;
      const shape = width <= 1024 && ratio <= 3 / 4 ? 'tall' : ratio > 3 / 4 && ratio <= 6 / 5 ? 'compact' : 'wide';
      return { width, height, left, layoutWidth, layoutHeight, shape,
        mismatch: String(Math.abs(layoutWidth - width) > 1 || left > 1) };
    };
    const visibleBannerHeight = (element, viewportHeight) => {
      if (!element || element.hidden || !element.isConnected || !element.getClientRects().length) return 0;
      const style = getComputedStyle(element);
      if (style.display === 'none' || style.visibility === 'hidden' || parseFloat(style.opacity) === 0) return 0;
      const bounds = element.getBoundingClientRect();
      const height = Math.min(bounds.bottom, viewportHeight) - Math.max(bounds.top, 0);
      return height > 0 ? Math.ceil(height) : 0;
    };
    const headerOffset = viewport => banners.reduce((height, banner) => height + visibleBannerHeight(banner, viewport.height), 0);
    const applyViewport = (viewport, offset) => {
      setStyle(root, '--eu-visible-width', `${viewport.width}px`);
      setStyle(root, '--eu-visible-left', `${viewport.left}px`);
      setStyle(root, '--quadro-visual-viewport-height', `${viewport.height}px`);
      setStyle(root, '--eu-header-offset', `${offset}px`);
      setAttribute(root, 'data-quadro-viewport-shape', viewport.shape);
      setAttribute(root, 'data-eu-visual-mismatch', viewport.mismatch);
      setAttribute(root, 'data-eu-viewport-ready', 'true');
    };
    const readHeaderGeometry = offset => {
      const bottom = Math.max(0, bar?.getBoundingClientRect().bottom || header.getBoundingClientRect().bottom);
      const shellStyle = getComputedStyle(shell);
      const shellBounds = shell.getBoundingClientRect();
      const top = Math.max(0, parseFloat(shellStyle.top) || 0);
      const sticky = shellStyle.position === 'sticky';
      const fixed = shellStyle.position === 'fixed';
      const occupied = fixed || (sticky && shellBounds.top <= top + 1) ? offset + Math.max(0, shellBounds.height) : offset;
      return { bottom, occupied, sticky, fixed };
    };
    const measure = () => {
      resizeFrame = 0;
      if (disposed || !header.isConnected) return;
      const viewport = viewportMetrics();
      const offset = headerOffset(viewport);
      const compact = compactQuery.matches || viewport.width <= 1024;
      const wasCollapsed = header.classList.contains('eu-nav-collapsed');
      // Reuse intrinsic desktop widths during ordinary scrolling. Invalidate
      // for dimensions, mode, fonts, content or observed component size changes.
      const fitKey = `${viewport.layoutWidth}:${viewport.layoutHeight}:${viewport.width}:${viewport.height}:${wasCollapsed}`;
      const probeDesktop = !compact && links && bar && (desktopFitDirty || desktopFit?.key !== fitKey);
      applyViewport(viewport, offset);
      let collapsed = compact;
      if (!compact && links && bar) {
        if (probeDesktop) header.classList.add('eu-nav-measuring');
        try {
          const style = getComputedStyle(bar);
          const layoutSpace = Math.min(bar.clientWidth, viewport.layoutWidth);
          const space = Math.max(0, Math.min(layoutSpace, viewport.width) - (parseFloat(style.paddingLeft) || 0) - (parseFloat(style.paddingRight) || 0));
          if (probeDesktop) {
            const gap = Math.ceil(parseFloat(style.columnGap) || 16);
            const required = Math.ceil(links.scrollWidth) + outerWidth(logo) + outerWidth(icons) + gap * 2 + 16;
            desktopFit = { key: fitKey, required };
            desktopFitDirty = false;
          }
          collapsed = wasCollapsed ? desktopFit.required + 8 > space : desktopFit.required > space;
        } finally {
          // Never leave the live logo/actions hidden across a painted frame.
          if (probeDesktop) header.classList.remove('eu-nav-measuring');
        }
      }
      if (wasCollapsed !== collapsed) header.classList.toggle('eu-nav-collapsed', collapsed);
      if (!collapsed) closeMobile(false);
      // This read may need layout after a REAL viewport/mode change. Keep it
      // synchronous so first paint, banner offsets and fold/unfold never use a
      // stale header height. Ordinary unchanged passes above make no writes.
      const geometry = readHeaderGeometry(offset);
      stickyShell = geometry.sticky;
      setStyle(root, '--eu-header-bottom', `${geometry.bottom}px`);
      setStyle(root, '--quadro-header-occupied-height', `${geometry.occupied}px`);
      if (document.body.classList.contains('template-index')) setStyle(document.body, 'padding-top', `${geometry.fixed ? geometry.occupied : 0}px`);
      if (!navReady) {
        navReady = true;
        setAttribute(header, 'data-nav-ready', 'true');
        header.dispatchEvent(new CustomEvent('quadro:navigation-ready', {
          bubbles: true,
          detail: { collapsed, shape: viewport.shape, visibleWidth: viewport.width, visibleHeight: viewport.height }
        }));
      }
    };
    const scheduleMeasure = () => { if (!disposed && !resizeFrame) resizeFrame = requestAnimationFrame(measure); };
    const invalidateDesktopFit = () => { desktopFitDirty = true; scheduleMeasure(); };
    const fitElements = new Set([bar, logo, icons, links].filter(Boolean));
    const resize = typeof ResizeObserver === 'function' ? new ResizeObserver(entries => {
      if (entries.some(entry => fitElements.has(entry.target))) desktopFitDirty = true;
      scheduleMeasure();
    }) : null;
    [shell, ...fitElements].filter(Boolean).forEach(element => resize?.observe(element));
    const headerContentChanges = new MutationObserver(invalidateDesktopFit);
    [links, logo, icons].filter(Boolean).forEach(element => headerContentChanges.observe(element, { childList: true, characterData: true, subtree: true }));
    const shellParent = shell.parentElement;
    const positionRoot = document.body || shellParent;
    const positionChanges = new MutationObserver(records => {
      const bannerChanged = records.some(record => record.type === 'childList' && [...record.addedNodes, ...record.removedNodes].some(node => node.nodeType === 1 && (node.matches?.('#geoip-banner, #banner-geofilter') || node.querySelector?.('#geoip-banner, #banner-geofilter'))));
      if (bannerChanged) observePosition();
      if (records.some(record => record.type === 'attributes' && record.target === shell)) desktopFitDirty = true;
      if (bannerChanged || records.some(record => record.type === 'attributes')) scheduleMeasure();
    });
    const observePosition = () => {
      positionChanges.disconnect();
      banners.forEach(banner => resize?.unobserve(banner));
      banners = Array.from(document.querySelectorAll('#geoip-banner, #banner-geofilter'));
      positionChanges.observe(shell, { attributes: true, attributeFilter: ['class', 'style', 'hidden'] });
      if (positionRoot) positionChanges.observe(positionRoot, { childList: true, subtree: true });
      banners.forEach(banner => {
        positionChanges.observe(banner, { attributes: true, attributeFilter: ['class', 'style', 'hidden'] });
        resize?.observe(banner);
      });
    };
    observePosition();
    on(window, 'resize', scheduleMeasure, { passive: true });
    on(window.visualViewport, 'resize', scheduleMeasure, { passive: true });
    on(window.visualViewport, 'scroll', scheduleMeasure, { passive: true });
    on(window, 'orientationchange', scheduleMeasure, { passive: true });
    on(window, 'pageshow', scheduleMeasure, { passive: true });
    onMedia(compactQuery, scheduleMeasure);
    on(shell, 'transitionend', event => { if (event.target === shell && ['top', 'transform', 'translate'].includes(event.propertyName)) scheduleMeasure(); });
    on(document, 'scroll', () => { if (openMenu || stickyShell) scheduleMeasure(); }, { passive: true });
    document.fonts?.ready.then(() => { if (!disposed && header.isConnected) invalidateDesktopFit(); });
    on(document.fonts, 'loadingdone', invalidateDesktopFit);
    // The R4 home bundle is non-blocking. Re-check hidden desktop links when
    // a stylesheet arrives too; ResizeObserver alone cannot measure display:none.
    on(document, 'load', event => { if (event.target instanceof HTMLLinkElement && event.target.relList.contains('stylesheet')) invalidateDesktopFit(); }, { capture: true });
    on(window, 'load', invalidateDesktopFit);
    on(toggle, 'click', () => openMenu ? closeMobile() : openMobile());
    on(menu, 'click', event => {
      const button = event.target.closest('.js-toggle-submenu');
      if (!button) return;
      let target;
      if (button.classList.contains('mobile-nav__return-btn')) {
        const current = stack.pop();
        renderMobile();
        target = Array.from(menu.querySelectorAll('[data-target]')).find(element => element.dataset.target === current?.dataset.parent && !element.classList.contains('mobile-nav__return-btn'));
      } else {
        const panel = Array.from(menu.querySelectorAll('[data-parent]')).find(element => element.dataset.parent === button.dataset.target);
        if (!panel) return;
        stack.push(panel);
        renderMobile();
        target = focusable(panel)[0];
      }
      target?.focus();
    });
    on(document, 'click', event => {
      if (!openMenu) return;
      if (!header.contains(event.target) || event.target.closest('[data-cart-drawer-open], .js-go-cart-trigger')) closeMobile(false);
    });
    on(document, 'keydown', event => {
      if (!openMenu) return;
      if (event.key === 'Escape') { event.preventDefault(); closeMobile(); }
      if (event.key === 'Tab') {
        const controls = [toggle, ...focusable(menu)];
        const index = controls.indexOf(document.activeElement);
        if (event.shiftKey && index <= 0) { event.preventDefault(); controls.at(-1)?.focus(); }
        else if (!event.shiftKey && index === controls.length - 1) { event.preventDefault(); toggle.focus(); }
      }
    });
    desktop?.querySelectorAll('[data-has-dropdowns]').forEach(item => {
      item.classList.remove('disabled');
      const button = item.querySelector(':scope > button');
      let timer;
      const open = () => { clearTimeout(timer); closeDesktop(item); item.classList.add('site-nav--active-dropdown'); button.setAttribute('aria-expanded', 'true'); };
      const close = () => { item.classList.remove('site-nav--active-dropdown'); button.setAttribute('aria-expanded', 'false'); };
      on(item, 'pointerenter', event => { if (event.pointerType !== 'touch') open(); });
      on(item, 'pointerleave', () => { timer = setTimeout(() => { if (!item.contains(document.activeElement)) close(); }, 150); });
      on(item, 'focusout', event => { if (!item.contains(event.relatedTarget)) close(); });
      on(button, 'click', event => {
        // A mouse arrives through pointerenter first; clicking must keep that menu open.
        const toggleIntent = event.detail === 0 || event.pointerType === 'touch';
        if (toggleIntent && item.classList.contains('site-nav--active-dropdown')) close();
        else open();
      });
      on(item, 'keydown', event => {
        if (event.key === 'Escape') { event.preventDefault(); close(); button.focus(); }
        if (event.key === 'ArrowDown' && event.target === button) { event.preventDefault(); open(); item.querySelector('.site-nav__dropdown a')?.focus(); }
      });
    });
    on(document, 'click', event => { if (!desktop?.contains(event.target)) closeDesktop(); });
    if (search) {
      const input = search.querySelector('[name="q"]');
      const triggers = header.querySelectorAll('.site-header__search-toggle');
      const stopSearch = () => window.QuadroPredictiveSearch?.close(search);
      triggers.forEach(trigger => on(trigger, 'click', () => { closeMobile(false); closeDesktop(); restoreSearchFocus = trigger; search.showModal(); triggers.forEach(button => button.setAttribute('aria-expanded', 'true')); document.documentElement.classList.add('eu-search-open'); input.focus(); }));
      on(search.querySelector('.js-drawer-close'), 'click', () => search.close());
      on(search, 'keydown', event => { if (event.key === 'Escape') { event.preventDefault(); search.close(); } });
      on(search, 'close', () => { stopSearch(); document.documentElement.classList.remove('eu-search-open'); triggers.forEach(button => button.setAttribute('aria-expanded', 'false')); restoreSearchFocus?.focus(); });
      on(search, 'click', event => { if (event.target !== search) return; const bounds = search.getBoundingClientRect(); if (event.clientY > bounds.bottom) search.close(); });
      on(abort.signal, 'abort', stopSearch);
    }
    renderMobile();
    measure();
    headers.set(header, () => { disposed = true; closeMobile(false); if (search?.open) search.close(); abort.abort(); mediaCleanups.forEach(cleanup => cleanup()); resize?.disconnect(); headerContentChanges.disconnect(); positionChanges.disconnect(); cancelAnimationFrame(resizeFrame); headers.delete(header); });
  }
  const closeDisclosure = disclosure => {
    disclosure.querySelector('button')?.setAttribute('aria-expanded', 'false');
    const list = disclosure.querySelector('[data-disclosure-list], .disclosure__list');
    if (list) { list.hidden = true; list.classList.remove('disclosure-list--visible'); }
  };
  const initialize = root => {
    if (root.matches?.('[data-header-section]')) bindHeader(root);
    root.querySelectorAll('[data-header-section]').forEach(bindHeader);
    root.querySelectorAll('.site-footer .disclosure').forEach(closeDisclosure);
    root.querySelectorAll('[data-header-section] a[href], .site-footer a[href]').forEach(link => {
      const url = safeURL(link.getAttribute('href'));
      if (url && url.hostname !== location.hostname) {
        link.target = '_blank'; link.relList.add('noopener', 'noreferrer');
      }
    });
    const returningSubscriber = new URLSearchParams(location.search).get('contact[tags]') === 'newsletter' && new URLSearchParams(location.search).get('form_type') === 'customer';
    root.querySelectorAll('.old-subscriber').forEach(element => element.classList.toggle('old-subscriber-true', returningSubscriber));
  };
  document.addEventListener('change', event => { if (event.target.matches('[data-quadro-language-selector]')) submit(event.target.form); });
  document.addEventListener('click', event => {
    const disclosure = event.target.closest('.site-footer .disclosure');
    document.querySelectorAll('.site-footer .disclosure').forEach(element => { if (element !== disclosure) closeDisclosure(element); });
    if (!disclosure) return;
    const button = event.target.closest('button');
    const list = disclosure.querySelector('[data-disclosure-list], .disclosure__list');
    if (button && list) { const open = button.getAttribute('aria-expanded') !== 'true'; button.setAttribute('aria-expanded', String(open)); list.hidden = !open; list.classList.toggle('disclosure-list--visible', open); }
    const option = event.target.closest('a[data-value]');
    if (option) { event.preventDefault(); const input = disclosure.querySelector('input[type="hidden"]'); if (input) { input.value = option.dataset.value; submit(input.form); } }
  });
  document.addEventListener('keydown', event => {
    const disclosure = event.target.closest('.site-footer .disclosure');
    if (disclosure && event.key === 'Escape') { closeDisclosure(disclosure); disclosure.querySelector('button').focus(); }
    if (disclosure && event.key === 'ArrowDown') { const list = disclosure.querySelector('[data-disclosure-list], .disclosure__list'); if (list?.hidden) { list.hidden = false; list.classList.add('disclosure-list--visible'); disclosure.querySelector('button').setAttribute('aria-expanded', 'true'); } event.preventDefault(); focusable(list)[0]?.focus(); }
  });
  let countdownTimer;
  const countdowns = () => {
    clearInterval(countdownTimer);
    if (document.hidden) return;
    const clocks = Array.from(document.querySelectorAll('.sale-countdown[data-sale-end]'));
    if (!clocks.length) return;
    const tick = () => clocks.forEach(clock => {
      const left = Math.max(0, Number(clock.dataset.saleEnd) - Math.floor(Date.now() / 1000));
      if (!left) { clock.style.display = 'none'; return; }
      clock.style.display = 'flex';
      if (clock.closest('[hidden]')) return;
      const timer = clock.querySelector('.sale-countdown-timer');
      if (timer) timer.textContent = `${Math.floor(left / 3600)}:${String(Math.floor(left % 3600 / 60)).padStart(2, '0')}:${String(left % 60).padStart(2, '0')}`;
    });
    tick(); countdownTimer = setInterval(tick, 1000);
  };
  let scrollFrame = 0;
  const topVisibility = () => { scrollFrame = 0; document.querySelectorAll('[data-eu-back-to-top]').forEach(button => { button.hidden = innerWidth <= 749 || scrollY <= 1000; }); };
  document.addEventListener('scroll', () => { if (!scrollFrame) scrollFrame = requestAnimationFrame(topVisibility); }, { passive: true });
  window.addEventListener('resize', () => { if (!scrollFrame) scrollFrame = requestAnimationFrame(topVisibility); }, { passive: true });
  document.addEventListener('click', event => { if (event.target.closest('[data-eu-back-to-top]')) window.scrollTo({ top: 0, behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' }); });
  document.addEventListener('visibilitychange', countdowns);
  document.addEventListener('shopify:section:load', event => { initialize(event.target); countdowns(); topVisibility(); });
  document.addEventListener('shopify:section:unload', event => { for (const [header, destroy] of headers) if (event.target.contains(header)) destroy(); countdowns(); });
  initialize(document); countdowns(); topVisibility();
  window.QuadroEUNavigation = { initialize };
})();
